import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import { api, ApiError } from '../lib/api';
import { useResolvedTheme } from '../lib/theme';
import * as track from '../lib/track';

// The moment credits run out (run 13): what the person can do about it, as buttons wherever the wall shows up (a Confirm
// card, an error box, Home, the credit banner). The server decides which buttons and what they say, from the same prices
// the Billing page reads (GET /portal/billing/wall); this file only draws them. The owner pays: a plan or a top-up on
// Billing, or the free credits for saving a card, right here. Anyone else is told whom to ask and can send them a notice.

export type WallAction = { id?: string; kind?: 'card' | 'notify_owner'; label: string; to?: string; primary?: boolean };
export type Wall = {
  available: number; plan: string; is_owner: boolean; owner: { name: string; email: string } | null; never_had_credits: boolean;
  needed: number | null; short: number | null; message: string; buttons: Array<WallAction & { id: string }>;
  card: { eligible: boolean; credits: number }; card_consent: string; publishable_key: string | null; configured: boolean;
};

/** The wall for a piece of work that needs `need` credits (null: the balance alone), or for a plan limit (gate). */
export function useWall(need: number | null, o: { gate?: boolean; enabled?: boolean; refetchMs?: number } = {}) {
  const qs = [need !== null ? `need=${Math.round(need)}` : '', o.gate ? 'gate=plan' : ''].filter(Boolean).join('&');
  return useQuery<Wall, ApiError>({
    queryKey: ['/portal/billing/wall', need, Boolean(o.gate)], queryFn: () => api<Wall>(`/portal/billing/wall${qs ? `?${qs}` : ''}`),
    enabled: o.enabled ?? true, refetchInterval: o.refetchMs, staleTime: 2000, retry: false,
  });
}

/** Funnel: this wall was shown (once per mount while `on`). A refused API call is counted by lib/api's observer instead. */
export function usePaywallSeen(on: boolean, wall: string, creditsShort: number | null) {
  useEffect(() => {
    if (!on) return;
    try { track.trackMoney('paywall_seen', wall, creditsShort != null ? { credits_short: creditsShort } : undefined); } catch { /* tracking never breaks the wall */ }
  }, [on]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** "48 credits needed, 0 available" (the server's refusal) as numbers. */
export function parseShort(message: string | undefined): { needed: number; available: number } | null {
  const m = /(\d[\d,]*) credits needed, (-?\d[\d,]*) available/.exec(message ?? '');
  return m ? { needed: Number(m[1]!.replace(/,/g, '')), available: Math.max(0, Number(m[2]!.replace(/,/g, ''))) } : null;
}

const kindOf = (a: WallAction) => a.kind ?? (a.id === 'card' ? 'card' : a.id === 'notify_owner' ? 'notify_owner' : undefined);

/**
 * The buttons. A page button is a link (Billing's own anchors); the card button opens the card form below the row; the
 * notice to the owner sends one email (once a day per person) and says it did.
 */
export function WallButtons({ actions, what, needed, small = true }: { actions: WallAction[]; what?: string; needed?: number | null; small?: boolean }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState<{ text: string; bad?: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const size = small ? ' btn-sm' : '';
  const notify = async () => {
    setBusy(true); setNote(null);
    try {
      const ask = [what, needed ? `${needed.toLocaleString('en-US')} credits needed` : null].filter(Boolean).join(': ');
      const r = await api<{ owner?: string; already?: boolean }>('/portal/billing/notify-owner', { method: 'POST', body: { need: 'credits', ...(ask ? { note: ask.slice(0, 300) } : {}) } });
      setNote({ text: r.already ? `${r.owner ?? 'The owner'} was already told today.` : `Sent${r.owner ? ` to ${r.owner}` : ''}. They get an email with a link to buy credits.` });
    } catch (e) { setNote({ text: (e as Error).message || 'The notice could not be sent.', bad: true }); } finally { setBusy(false); }
  };
  return (
    <>
      <span className="actions wall-actions" style={{ marginTop: 8 }}>
        {actions.map((a) => {
          const k = kindOf(a);
          const cls = `btn${size}${a.primary ? ' btn-primary' : ''}`;
          if (k === 'card') return <button key={a.label} type="button" className={cls} aria-expanded={open} onClick={() => setOpen(!open)}>{a.label}</button>;
          if (k === 'notify_owner') return <button key={a.label} type="button" className={cls} disabled={busy || Boolean(note && !note.bad)} onClick={() => void notify()}>{busy ? 'Sending…' : a.label}</button>;
          return a.to ? <Link key={a.label} className={cls} to={a.to}>{a.label}</Link> : null;
        })}
      </span>
      {note ? <span className={`small ${note.bad ? 'error' : 'muted'}`} role="status" style={{ display: 'block', marginTop: 6 }}>{note.text}</span> : null}
      {open ? <CardForCredits onClose={() => setOpen(false)} /> : null}
    </>
  );
}

/** A wall already read: its message and its buttons. */
export function WallBlock({ wall, what, seen = 'confirm_card' }: { wall: Wall; what?: string; seen?: string | null }) {
  usePaywallSeen(seen !== null, seen ?? '', wall.short);
  return (
    <div className="credit-wall" role="group" aria-label="Get more credits">
      <p className="small" style={{ margin: '6px 0 0' }}>{wall.message}</p>
      <WallButtons actions={wall.buttons} what={what} needed={wall.needed} />
    </div>
  );
}

/** On Billing: the free credits for saving a card, as a button, while this workspace may still have them. */
export function CardOffer() {
  const w = useWall(null);
  const b = w.data?.is_owner ? w.data.buttons.find((x) => x.id === 'card') : undefined;
  if (!w.data || !b) return null;
  return (
    <div className="credit-wall" role="group" aria-label="Free credits for a saved card">
      <p className="small" style={{ margin: '6px 0 0' }}>Free credits: save a card and get {w.data.card.credits.toLocaleString('en-US')} credits, once. {w.data.card_consent}</p>
      <WallButtons actions={[b]} small={false} />
    </div>
  );
}

/** The message and the buttons for work that needs `need` credits (or a plan limit with gate). */
export function CreditWall({ need, gate = false, what }: { need: number | null; gate?: boolean; what?: string }) {
  const w = useWall(need, { gate, refetchMs: 8000 });
  return w.data ? <WallBlock wall={w.data} what={what} seen={null} /> : null;
}

// The card, for the free credits -------------------------------------------------------------------------------------

let stripePromise: Promise<Stripe | null> | null = null;
const loadStripeOnce = (key: string) => (stripePromise ??= import('@stripe/stripe-js').then((m) => m.loadStripe(key)));

const appearance = (dark: boolean): StripeElementsOptions['appearance'] => ({
  theme: dark ? 'night' : 'stripe',
  variables: dark
    ? { colorPrimary: '#7fb0ff', colorText: '#e3e7ef', colorBackground: '#161a22', colorDanger: '#f08a7e', borderRadius: '12px', spacingUnit: '4px' }
    : { colorPrimary: '#1769e0', colorText: '#1c2230', colorDanger: '#b42318', borderRadius: '12px', spacingUnit: '4px' },
});

type Started = { client_secret: string; setup_intent: string; credits: number; publishable_key: string | null; consent: string };

/** Saves a card in Stripe's Payment Element (setup mode: nothing is charged) and adds the free credits, once per card. */
export function CardForCredits({ onClose }: { onClose?: () => void }) {
  const qc = useQueryClient();
  const theme = useResolvedTheme();
  const [s, setS] = useState<Started | null>(null);
  const [mod, setMod] = useState<null | typeof import('@stripe/react-stripe-js')>(null);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await api<Started>('/portal/billing/card-credits', { method: 'POST' });
        if (!r.publishable_key) throw new Error('Card payments are being switched on. Check back shortly.');
        const [m, st] = await Promise.all([import('@stripe/react-stripe-js'), loadStripeOnce(r.publishable_key)]);
        if (live) { setS(r); setMod(m); setStripe(st); }
      } catch (e) { if (live) setErr((e as Error).message || 'The card form did not load. Try again.'); }
    })();
    return () => { live = false; };
  }, []);
  if (done) return <p className="small wall-card" role="status" style={{ flexBasis: '100%' }}>✓ {done}</p>;
  if (err) return <p className="error small wall-card" role="alert">{err}</p>;
  if (!s || !mod || !stripe) return <p className="muted small wall-card" role="status">Loading the card form…</p>;
  const { Elements } = mod;
  return (
    <div className="wall-card" style={{ marginTop: 10, maxWidth: 480 }}>
      <p className="small"><b>Save a card, get {s.credits.toLocaleString('en-US')} free credits.</b> Once per card. {s.consent}</p>
      <Elements key={theme} stripe={stripe} options={{ clientSecret: s.client_secret, appearance: appearance(theme === 'dark') }}>
        <CardForm mod={mod} started={s} onDone={(t) => { setDone(t); void qc.invalidateQueries(); }} onClose={onClose} />
      </Elements>
    </div>
  );
}

function CardForm({ mod, started, onDone, onClose }: { mod: typeof import('@stripe/react-stripe-js'); started: Started; onDone: (text: string) => void; onClose?: () => void }) {
  const stripe = mod.useStripe();
  const elements = mod.useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setErr(null);
    // 3-D Secure opens in a modal; only a bank redirect leaves the page
    const r = await stripe.confirmSetup({ elements, redirect: 'if_required', confirmParams: { return_url: `${location.origin}/billing` } });
    if (r.error) { setErr(r.error.message ?? 'The card was not saved.'); setBusy(false); return; }
    try {
      const c = await api<{ status: string; text: string }>('/portal/billing/card-credits/confirm', { method: 'POST', body: { setup_intent: started.setup_intent } });
      if (c.status === 'succeeded') onDone(c.text); else setErr('Stripe has not confirmed the card yet. Try again in a moment.');
    } catch (x) { setErr((x as Error).message); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="pay-form" aria-label="Save a card">
      <mod.PaymentElement options={{ layout: 'tabs' }} />
      {err ? <p className="error small" role="alert">{err}</p> : null}
      <div className="actions" style={{ marginTop: 10 }}>
        <button type="submit" className="btn btn-primary" disabled={!stripe || busy}>{busy ? 'Saving…' : `Save card, get ${started.credits.toLocaleString('en-US')} credits`}</button>
        {onClose ? <button type="button" className="btn" onClick={onClose} disabled={busy}>Not now</button> : null}
      </div>
      <p className="small muted">Payments are processed by Stripe. Outlevo never sees your card details.</p>
    </form>
  );
}
